import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { toCamelCase } from 'utils/strings'

import {
  DELIVERY,
  PARTICIPANTS_ALL_CATEGORY,
  PARTICIPANTS_EXCLUDE_FILTER_VALUES,
  PARTICIPANTS_NAMES_CATEGORY,
  PARTICIPANTS_PER_PAGE,
} from 'containers/AwarenessCenter/constants'

import {
  CHANGE_DELIVERY,
  CHANGE_PARTICIPANTS_SELECTION,
  CHANGE_PARTICIPANTS_TOTAL_SELECTION,
  GET_CAMPAIGN_ASYNC,
} from '../constants'
import { selectParticipants, selectSelectedDelivery } from '../selectors'

import {
  getParticipantsError,
  getParticipantsInfoError,
  getParticipantsInfoSuccess,
  getParticipantsSuccess,
  searchParticipantsInfoError,
  searchParticipantsInfoSuccess,
} from './actions'
import {
  FETCH_QUICK_LAUNCH_ASYNC,
  GET_PARTICIPANTS_ASYNC,
  GET_PARTICIPANTS_INFO_ASYNC,
  SEARCH_PARTICIPANTS_INFO_ASYNC,
  SUBMIT_QUICK_LAUNCH_ASYNC,
} from './constants'

const getParticipantsInfoEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_PARTICIPANTS_INFO_ASYNC.INITIATE, CHANGE_DELIVERY),
    mergeMap(() =>
      api.wizardCampaign
        .getParticipantsInfo(state$.value.partnerOptions.selectedCompany?.value, {
          deliveryType: selectSelectedDelivery(state$.value),
          groups: selectParticipants(state$.value).included,
          excludedGroups: selectParticipants(state$.value).excluded,
        })
        .pipe(
          mergeMap((response) => of(getParticipantsInfoSuccess(response.response))),
          catchError((error) => of(epicsErrorHandler(getParticipantsInfoError, error, [400, 403]))),
          takeUntil(action$.pipe(ofType(GET_PARTICIPANTS_INFO_ASYNC.CANCEL)))
        )
    )
  )

const searchParticipantsInfoEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SEARCH_PARTICIPANTS_INFO_ASYNC.INITIATE),
    mergeMap((action) =>
      api.wizardCampaign
        .getParticipantsInfo(state$.value.partnerOptions.selectedCompany?.value, {
          searchFilter: action.payload.value,
          deliveryType: selectSelectedDelivery(state$.value),
          groups: selectParticipants(state$.value).included,
          excludedGroups: selectParticipants(state$.value).excluded,
        })
        .pipe(
          mergeMap((response) => of(searchParticipantsInfoSuccess(response.response))),
          catchError((error) =>
            of(epicsErrorHandler(searchParticipantsInfoError, error, [400, 403]))
          ),
          takeUntil(action$.pipe(ofType(SEARCH_PARTICIPANTS_INFO_ASYNC.CANCEL)))
        )
    )
  )

const fetchQuickLaunchEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_QUICK_LAUNCH_ASYNC.INITIATE),
    mergeMap(() =>
      api.wizardCampaign.getQuickLaunch(state$.value.partnerOptions.selectedCompany?.value).pipe(
        mergeMap((response) =>
          of(
            FETCH_QUICK_LAUNCH_ASYNC.onSuccess({
              data: response,
            })
          )
        ),
        catchError((error) =>
          of(epicsErrorHandler(FETCH_QUICK_LAUNCH_ASYNC.onError, error, [400]))
        ),
        takeUntil(action$.pipe(ofType(FETCH_QUICK_LAUNCH_ASYNC.CANCEL)))
      )
    )
  )

const submitQuickLaunchEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SUBMIT_QUICK_LAUNCH_ASYNC.INITIATE),
    mergeMap((action) =>
      api.wizardCampaign
        .submitQuickLaunch(state$.value.partnerOptions.selectedCompany?.value, action.payload.data)
        .pipe(
          mergeMap(({ response }) =>
            of(
              SUBMIT_QUICK_LAUNCH_ASYNC.onSuccess({
                data: response,
              })
            )
          ),
          catchError((error) =>
            of(epicsErrorHandler(SUBMIT_QUICK_LAUNCH_ASYNC.onError, error, [400]))
          ),
          takeUntil(action$.pipe(ofType(SUBMIT_QUICK_LAUNCH_ASYNC.CANCEL)))
        )
    )
  )

const getParticipantsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_CAMPAIGN_ASYNC.SUCCESS,
      GET_PARTICIPANTS_INFO_ASYNC.SUCCESS,
      GET_PARTICIPANTS_ASYNC.INITIATE,
      CHANGE_PARTICIPANTS_SELECTION,
      CHANGE_PARTICIPANTS_TOTAL_SELECTION
    ),
    switchMap((action) => {
      let showIncluded
      let showExcluded
      if (
        action.type === GET_PARTICIPANTS_INFO_ASYNC.SUCCESS ||
        action.type === CHANGE_PARTICIPANTS_TOTAL_SELECTION
      ) {
        showIncluded = true
        showExcluded = false
      } else {
        // modal case
        const excludeFilter = selectParticipants(state$.value).excludeFilter
        showIncluded =
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.ALL ||
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.INCLUDED
        showExcluded =
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.ALL ||
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.EXCLUDED
      }

      const participants = selectParticipants(state$.value)
      const include = {
        allCompany: false,
        show: showIncluded,
      }
      const exclude = {
        show: showExcluded,
      }

      Object.keys(participants.included).forEach((key) => {
        const formattedKey = toCamelCase(key)

        if (key === PARTICIPANTS_ALL_CATEGORY) {
          include.allCompany = true
        } else if (key === PARTICIPANTS_NAMES_CATEGORY) {
          include.emails = participants.included[key]
        } else {
          include[formattedKey] = participants.included[key]
        }
      })

      Object.keys(participants.excluded).forEach((key) => {
        const formattedKey = toCamelCase(key)

        if (key === PARTICIPANTS_NAMES_CATEGORY) {
          exclude.emails = participants.excluded[key]
        } else {
          exclude[formattedKey] = participants.excluded[key]
        }
      })

      if (include.emails?.length === 0) delete include.emails
      if (exclude.emails?.length === 0) delete exclude.emails

      return api.wizardCampaign
        .getParticipants(
          state$.value.partnerOptions.selectedCompany?.value,
          {
            search: selectParticipants(state$.value).modalSearch,
            deliveryType: DELIVERY.EMAIL,
            include,
            exclude,
          },
          selectParticipants(state$.value).page,
          PARTICIPANTS_PER_PAGE
        )
        .pipe(
          mergeMap((response) => of(getParticipantsSuccess(response.response))),
          catchError((error) => of(epicsErrorHandler(getParticipantsError, error, [400]))),
          takeUntil(action$.pipe(ofType(GET_PARTICIPANTS_ASYNC.CANCEL)))
        )
    })
  )

export default combineEpics(
  getParticipantsInfoEpic,
  fetchQuickLaunchEpic,
  submitQuickLaunchEpic,
  getParticipantsEpic,
  searchParticipantsInfoEpic
)
